// frontend/src/components/Layout.js
import React, { useMemo, useState } from 'react';
import Sidebar, { SIDEBAR_COLLAPSED_DRAWER_WIDTH, SIDEBAR_DRAWER_WIDTH } from './Sidebar';
import Navigation from './Navigation';
import Footer from './Footer';
import { Box, CssBaseline, useMediaQuery } from '@mui/material';
import BreadcrumbComponent from './BreadcrumbComponent';
import ScrollToTop from './ScrollToTop'; // Import the new component
import { useTheme, alpha } from '@mui/material/styles';
import { BRAND } from '../theme';

const SIDEBAR_COLLAPSE_KEY = 'iv-linx-sidebar-collapsed';
const AUTO_HIDE_SIDEBAR_MAX_WIDTH = 1600;


const Layout = ({ children }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const shouldAutoHideSidebar = useMediaQuery(`(max-width:${AUTO_HIDE_SIDEBAR_MAX_WIDTH}px)`);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === 'true';
        } catch (error) {
            return false;
        }
    });

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    const handleSidebarCollapseToggle = () => {
        setIsSidebarCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SIDEBAR_COLLAPSE_KEY, String(next));
            } catch (error) {
                // Ignore localStorage failures in private mode.
            }
            return next;
        });
    };

    const sidebarDesktopWidth = useMemo(() => {
        if (shouldAutoHideSidebar) return 0;
        return isSidebarCollapsed ? SIDEBAR_COLLAPSED_DRAWER_WIDTH : SIDEBAR_DRAWER_WIDTH;
    }, [isSidebarCollapsed, shouldAutoHideSidebar]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <CssBaseline />
            <Navigation
                handleDrawerToggle={handleDrawerToggle}
                showDesktopDrawerToggle={shouldAutoHideSidebar}
                desktopSidebarWidth={sidebarDesktopWidth}
            />
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, minWidth: 0 }}>
                <Sidebar
                    mobileOpen={mobileOpen}
                    handleDrawerToggle={handleDrawerToggle}
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={handleSidebarCollapseToggle}
                    shouldAutoHideSidebar={shouldAutoHideSidebar}
                />
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        padding: {
                            xs: 1.25,
                            md: shouldAutoHideSidebar ? 1.25 : 1.5,
                            xl: shouldAutoHideSidebar ? 1.5 : 1.75,
                        },
                        background: `linear-gradient(180deg, ${BRAND.surface} 0%, ${BRAND.surfaceAlt} 100%)`,
                        minHeight: 0,
                        minWidth: 0,
                        overflowX: 'hidden',
                        overflowY: 'visible',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ minHeight: { xs: 52, sm: 54 } }} />
                    <ScrollToTop /> {/* Insert ScrollToTop here */}
                    {/* Ensure this Box aligns content properly */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.75,
                            mt: 0.25,
                            px: 0.25,
                            py: 0.25,
                            borderBottom: `1px solid ${alpha(BRAND.dark, 0.08)}`,
                        }}
                    >
                        <BreadcrumbComponent />
                        {/* Add any other components (like title) here, and they will align properly */}
                    </Box>
                    {children}
                </Box>
            </Box>
            <Box sx={{ ml: { xs: 0, sm: `${sidebarDesktopWidth}px` }, transition: 'margin-left 200ms ease' }}>
                <Footer />
            </Box>
        </Box>
    );
};

export default Layout;
